import type { Action, Status } from '@skop-app/lib-domain/types'

import type { SelectOption } from '@/services/property/option.normalized'

export type InventoryStatusGroup = {
  id: string
  label: string
  value: string
  options: InventoryStatusActionOption[]
}

export type InventoryStatusActionOption = {
  id: string
  label: string
  value: string
  statusType: string
  isDefault?: boolean
  icon?: string
  isQuickAction?: boolean
}

export type InventoryStatusTypeOption = {
  label: string
  value: string
}

export type InventoryStatusFamily =
  | 'creation'
  | 'available'
  | 'processing'
  | 'removed'

export type InventoryQuickStatusAction = {
  key: string
  label: string
  icon?: string
  payload: {
    type: Status
    action: Action
  }
}

export type InventoryStatusCatalog = {
  actionGroups: InventoryStatusGroup[]
  actionOptions: InventoryStatusActionOption[]
  actionOptionByValue: Record<string, InventoryStatusActionOption>
  actionValuesByFamily: Partial<Record<InventoryStatusFamily, string[]>>
  actionValuesByType: Record<string, string[]>
  defaultAction?: InventoryStatusActionOption
  typeOptions: InventoryStatusTypeOption[]
  quickActions: InventoryQuickStatusAction[]
}

type InventoryStatusFilterParams = {
  includeTypes?: readonly string[]
  excludeTypes?: readonly string[]
  onlyQuickActions?: boolean
}

type InventoryStatusDefaultParams = InventoryStatusFilterParams & {
  preferredValues?: readonly string[]
}

const INVENTORY_CREATION_FALLBACK_ACTION_VALUES = ['created', 'creation']

const EMPTY_ACTION_GROUPS: InventoryStatusGroup[] = []
const EMPTY_ACTION_OPTIONS: InventoryStatusActionOption[] = []
const EMPTY_ACTION_OPTION_LOOKUP: Record<string, InventoryStatusActionOption> =
  {}
const EMPTY_ACTION_VALUES_BY_FAMILY: Partial<
  Record<InventoryStatusFamily, string[]>
> = {}
const EMPTY_ACTION_VALUES_BY_TYPE: Record<string, string[]> = {}
const EMPTY_TYPE_OPTIONS: InventoryStatusTypeOption[] = []
const EMPTY_QUICK_ACTIONS: InventoryQuickStatusAction[] = []

export const EMPTY_INVENTORY_STATUS_CATALOG: InventoryStatusCatalog = {
  actionGroups: EMPTY_ACTION_GROUPS,
  actionOptions: EMPTY_ACTION_OPTIONS,
  actionOptionByValue: EMPTY_ACTION_OPTION_LOOKUP,
  actionValuesByFamily: EMPTY_ACTION_VALUES_BY_FAMILY,
  actionValuesByType: EMPTY_ACTION_VALUES_BY_TYPE,
  typeOptions: EMPTY_TYPE_OPTIONS,
  quickActions: EMPTY_QUICK_ACTIONS,
}

const INVENTORY_STATUS_FAMILY_BY_TYPE: Record<string, InventoryStatusFamily> = {
  creation: 'creation',
  processing: 'processing',
  distribution: 'available',
  available: 'available',
  sale: 'available',
  done: 'removed',
  deleted: 'removed',
}

const LEGACY_INVENTORY_ACTION_TYPE_BY_ACTION: Record<string, string> = {
  created: 'creation',
  purchased: 'creation',
  diagnostic: 'creation',
  imported: 'creation',
  pricing: 'processing',
  destroy: 'processing',
  refurbish: 'processing',
  repair: 'processing',
  clean: 'processing',
  move: 'processing',
  use: 'processing',
  recycle: 'processing',
  reserved: 'processing',
  stock: 'available',
  available: 'available',
  distribution: 'distribution',
  sale: 'sale',
  give: 'sale',
  lent: 'done',
  given: 'done',
  sold: 'done',
  recycled: 'done',
  destroyed: 'done',
  creation: 'creation',
  processing: 'processing',
  done: 'done',
  deleted: 'deleted',
}

const hasStatusChildren = (
  group: SelectOption,
): group is SelectOption & {
  label: string
  value: string
  options: SelectOption[]
} =>
  typeof group.label === 'string' &&
  typeof group.value === 'string' &&
  Array.isArray(group.options) &&
  group.options.length > 0

const hasStatusActionValue = (
  option: SelectOption,
): option is SelectOption & { label: string; value: string } =>
  typeof option.label === 'string' && typeof option.value === 'string'

const toGroupedSelectOption = (
  group: InventoryStatusGroup,
): InventoryStatusGroup => ({
  id: group.id,
  label: group.label,
  value: group.value,
  options: group.options,
})

const matchesStatusFilter = (
  option: Pick<InventoryStatusActionOption, 'statusType' | 'isQuickAction'>,
  params: InventoryStatusFilterParams,
): boolean => {
  if (params.includeTypes?.length) {
    if (!params.includeTypes.includes(option.statusType)) return false
  } else if (
    params.excludeTypes?.length &&
    params.excludeTypes.includes(option.statusType)
  ) {
    return false
  }

  if (params.onlyQuickActions && option.isQuickAction !== true) {
    return false
  }

  return true
}

const hasStatusFilter = (params: InventoryStatusFilterParams): boolean =>
  Boolean(
    params.includeTypes?.length ||
    params.excludeTypes?.length ||
    params.onlyQuickActions,
  )

const findInventoryCreationFallbackAction = (
  candidates: readonly InventoryStatusActionOption[],
): InventoryStatusActionOption | undefined => {
  for (const fallbackValue of INVENTORY_CREATION_FALLBACK_ACTION_VALUES) {
    const fallbackOption = candidates.find(
      (option) =>
        option.value === fallbackValue && option.statusType === 'creation',
    )
    if (fallbackOption) return fallbackOption
  }

  return candidates.find((option) => option.statusType === 'creation')
}

export const buildInventoryStatusCatalog = (
  actionGroups: readonly SelectOption[] | undefined,
): InventoryStatusCatalog => {
  if (!actionGroups?.length) return EMPTY_INVENTORY_STATUS_CATALOG

  const normalizedGroups: InventoryStatusGroup[] = []
  const actionOptions: InventoryStatusActionOption[] = []
  const actionOptionByValue: Record<string, InventoryStatusActionOption> = {}
  const actionValuesByFamily: Partial<Record<InventoryStatusFamily, string[]>> =
    {}
  const actionValuesByType: Record<string, string[]> = {}
  const typeOptions: InventoryStatusTypeOption[] = []
  let defaultAction: InventoryStatusActionOption | undefined

  actionGroups.forEach((group) => {
    if (!hasStatusChildren(group)) return

    const groupOptions: InventoryStatusActionOption[] = []

    group.options.forEach((child) => {
      if (!hasStatusActionValue(child)) return
      if (actionOptionByValue[child.value]) return

      const actionOption: InventoryStatusActionOption = {
        id: child.id,
        label: child.label,
        value: child.value,
        statusType: group.value,
        ...(child.isDefault ? { isDefault: child.isDefault } : {}),
        ...(child.icon ? { icon: child.icon } : {}),
        ...(child.isQuickAction ? { isQuickAction: child.isQuickAction } : {}),
      }

      actionOptionByValue[actionOption.value] = actionOption
      actionOptions.push(actionOption)
      groupOptions.push(actionOption)
      actionValuesByType[group.value] = [
        ...(actionValuesByType[group.value] ?? []),
        actionOption.value,
      ]
      const statusFamily = resolveInventoryStatusFamily(group.value)
      if (statusFamily) {
        actionValuesByFamily[statusFamily] = [
          ...(actionValuesByFamily[statusFamily] ?? []),
          actionOption.value,
        ]
      }

      if (!defaultAction && actionOption.isDefault) {
        defaultAction = actionOption
      }
    })

    if (groupOptions.length === 0) return

    normalizedGroups.push({
      id: group.id,
      label: group.label,
      value: group.value,
      options: groupOptions,
    })
    typeOptions.push({
      label: group.label,
      value: group.value,
    })
  })

  if (actionOptions.length === 0) return EMPTY_INVENTORY_STATUS_CATALOG

  const quickActions = actionOptions.flatMap((option) =>
    option.isQuickAction
      ? [
          {
            key: `status-${option.statusType}-${option.value}`,
            label: option.label,
            icon: option.icon,
            payload: {
              type: option.statusType as Status,
              action: option.value as Action,
            },
          },
        ]
      : [],
  )

  return {
    actionGroups: normalizedGroups,
    actionOptions,
    actionOptionByValue,
    actionValuesByFamily,
    actionValuesByType,
    defaultAction,
    typeOptions,
    quickActions,
  }
}

export const getInventoryActionGroups = (
  catalog: InventoryStatusCatalog,
  params: InventoryStatusFilterParams = {},
): InventoryStatusGroup[] =>
  catalog.actionGroups.flatMap((group) => {
    const options = group.options.filter((option) => {
      const catalogOption = catalog.actionOptionByValue[String(option.value)]
      return catalogOption ? matchesStatusFilter(catalogOption, params) : false
    })

    if (options.length === 0) return []

    return [toGroupedSelectOption({ ...group, options })]
  })

export const getInventoryActionOptions = (
  catalog: InventoryStatusCatalog,
  params: InventoryStatusFilterParams = {},
): InventoryStatusActionOption[] =>
  catalog.actionOptions.filter((option) => matchesStatusFilter(option, params))

export const getInventoryActionValues = (
  catalog: InventoryStatusCatalog,
  params: InventoryStatusFilterParams = {},
): string[] =>
  getInventoryActionOptions(catalog, params).map((option) => option.value)

export const getInventoryActionValuesForType = (
  catalog: InventoryStatusCatalog,
  statusType: string,
): string[] => catalog.actionValuesByType[statusType] ?? []

export const getInventoryActionValuesForFamily = (
  catalog: InventoryStatusCatalog,
  statusFamily: InventoryStatusFamily,
): string[] => catalog.actionValuesByFamily[statusFamily] ?? []

export const getInventoryDefaultActionOption = (
  catalog: InventoryStatusCatalog,
  params: InventoryStatusDefaultParams = {},
): InventoryStatusActionOption | undefined => {
  const candidates = getInventoryActionOptions(catalog, params)
  if (candidates.length === 0) return undefined

  if (params.preferredValues?.length) {
    for (const preferredValue of params.preferredValues) {
      const preferredOption = candidates.find(
        (option) => option.value === preferredValue,
      )
      if (preferredOption) return preferredOption
    }
  }

  if (
    catalog.defaultAction &&
    candidates.some((option) => option.value === catalog.defaultAction?.value)
  ) {
    return catalog.defaultAction
  }

  const creationFallback = findInventoryCreationFallbackAction(candidates)
  if (creationFallback) return creationFallback

  if (!hasStatusFilter(params)) return undefined

  return candidates[0]
}

export const findInventoryActionOption = (
  actionValue: string | undefined,
  catalog: InventoryStatusCatalog,
): InventoryStatusActionOption | undefined =>
  actionValue ? catalog.actionOptionByValue[actionValue] : undefined

export const getInventoryQuickStatusActions = (
  catalog: InventoryStatusCatalog,
): InventoryQuickStatusAction[] => catalog.quickActions

export const resolveInventoryActionType = (
  actionValue: string | undefined,
  catalog: InventoryStatusCatalog,
): Status | 'deleted' | undefined => {
  if (!actionValue) return undefined
  if (actionValue === 'deleted') return 'deleted'
  const statusType = catalog.actionOptionByValue[actionValue]?.statusType
  if (statusType) return statusType as Status | undefined
  const legacyStatusType = LEGACY_INVENTORY_ACTION_TYPE_BY_ACTION[actionValue]
  return legacyStatusType as Status | 'deleted' | undefined
}

export const resolveInventoryStatusFamily = (
  statusType: string | undefined,
): InventoryStatusFamily | null => {
  if (!statusType) return null
  return INVENTORY_STATUS_FAMILY_BY_TYPE[statusType] ?? null
}

export const resolveInventoryActionFamily = (
  actionValue: string | undefined,
  catalog: InventoryStatusCatalog,
): InventoryStatusFamily | null =>
  resolveInventoryStatusFamily(resolveInventoryActionType(actionValue, catalog))

export const countInventoryStatusTypes = (
  countsByAction: Record<string, number>,
  catalog: InventoryStatusCatalog,
): Record<string, number> => {
  const countsByType: Record<string, number> = {}

  Object.entries(countsByAction).forEach(([actionValue, count]) => {
    if (!count) return

    const statusType = resolveInventoryActionType(actionValue, catalog)
    if (!statusType) return

    countsByType[statusType] = (countsByType[statusType] ?? 0) + count
  })

  return countsByType
}

export const countInventoryStatusFamilies = (
  countsByAction: Record<string, number>,
  catalog: InventoryStatusCatalog,
): Partial<Record<InventoryStatusFamily, number>> => {
  const countsByFamily: Partial<Record<InventoryStatusFamily, number>> = {}

  Object.entries(countsByAction).forEach(([actionValue, count]) => {
    if (!count) return

    const family = resolveInventoryActionFamily(actionValue, catalog)
    if (!family) return

    countsByFamily[family] = (countsByFamily[family] ?? 0) + count
  })

  return countsByFamily
}
